import React, { useCallback } from 'react';

import { AccountScreenContainer, LoadingAnimation } from '@order/components';
import { useTelemetry } from '@order/Telemetry';

import type { SweetpassMembershipScreenProps } from '../../navigation/AppNavigation.props';
import {
  useSweetpassMembershipContent,
  useSweetpassMembershipFailureEvent,
  useSweetpassMembershipSuccessEvent,
} from './hooks';
import { SweetpassMembershipScreenContent } from './SweetpassMembershipScreenContent';

export const SweetpassMembershipScreen = ({
  navigation,
}: SweetpassMembershipScreenProps) => {
  const { track } = useTelemetry();

  const subscribeHandler = useCallback(async () => {
    navigation.navigate('Modal', { screen: 'SweetpassUpgrade' });
    track('sweetpass_account.upgrade_cta.tapped');
  }, [navigation, track]);

  const viewPaymentMethodHandler = useCallback(
    (paymentMethodId: string) => {
      navigation.navigate('Modal', {
        screen: 'SweetpassPaymentMethodEdit',
        params: { paymentMethodId },
      });
    },
    [navigation],
  );

  const addPaymentMethodHandler = useCallback(() => {
    navigation.navigate('Modal', { screen: 'SweetpassPaymentMethodAdd' });
    track('sweetpass_account.add_payment_cta.tapped');
  }, [navigation, track]);

  const viewBillingHistoryHandler = useCallback(() => {
    navigation.navigate('Modal', { screen: 'SweetpassBillingHistory' });
    track('sweetpass_account.billing_history.tapped');
  }, [navigation, track]);

  const {
    isSweetpassEnabled,
    isSweetpassMembershipLoading,
    isSweetpassContentLoading,
    sweetpassMembershipContent,
    tier,
    loyaltyProfileError,
  } = useSweetpassMembershipContent({
    subscribeHandler,
    viewPaymentMethodHandler,
    addPaymentMethodHandler,
    viewBillingHistoryHandler,
  });

  useSweetpassMembershipSuccessEvent({
    isLoading: isSweetpassMembershipLoading,
    isEnabled: isSweetpassEnabled,
    hasLoyaltyProfile: Boolean(tier) && !loyaltyProfileError,
  });

  useSweetpassMembershipFailureEvent({
    isLoading: isSweetpassMembershipLoading,
    isEnabled: isSweetpassEnabled,
    hasLoyaltyProfile: Boolean(tier) && !loyaltyProfileError,
    loyaltyProfileError,
  });

  const isLoading = isSweetpassMembershipLoading || isSweetpassContentLoading;

  const id = 'sweetpass-membership';

  const testID = id;

  return (
    <AccountScreenContainer testID={`${testID}.wrapper`} screen={id}>
      {isLoading ? (
        <LoadingAnimation testID={`${testID}.loading`} />
      ) : isSweetpassEnabled ? (
        <SweetpassMembershipScreenContent
          testID={testID}
          sweetpassMembershipContent={sweetpassMembershipContent}
        />
      ) : null}
    </AccountScreenContainer>
  );
};
